import { Box3, Intersection, Object3D, Raycaster, Vector3 } from 'three';

import { isDefined } from '../../../../utils/variables';
import { Cache } from '../../../cache/cache-manager';
import { SkyviewCamera } from '../../../cameras/types';
import { CesiumLoader } from '../../../loader/cesium/cesium-loader';
import { CesiumTileSetHierarchy } from '../../../loader/cesium/tile-set';
import { TileNodeLoadResponse } from '../../../loader/cesium/tile-set/queue-chunks-to-load';
import { TileNode } from '../../../loader/cesium/tile-set/types';
import {
  createTileNodeDataUri,
  extractTileNodeBoundingSphere,
  getNodeWithContent,
} from '../../../loader/cesium/tile-set/utilities';
import { toPointPositions } from '../../../utilities/pick-near-cursor';
import { getWebWorkerManager } from '../../../workers/web-worker-manager';
import { Tiled3dModel } from '../type';

/**
 *  Cesium point cloud handler.
 */
export class Cesium3dTiles extends Tiled3dModel {
  private boundingBox: Box3 = new Box3();

  private cache: Cache;

  private lastTick: number = Date.now();

  private tileNodeLoadQueue: Map<string, TileNode> = new Map<string, TileNode>();
  private maxLoadQueueSize = 6;

  private geometryBoundingBox: Box3 = new Box3();

  getType(): '3d-tiles-renderer' | 'skymap-tiles-renderer' | 'gltf-tiles-renderer' {
    return 'skymap-tiles-renderer';
  }

  constructor(
    private cesiumTileSetHierarchy: CesiumTileSetHierarchy,
    readonly origo: Vector3,
  ) {
    super();
    this.cache = new Cache();

    getWebWorkerManager().init('tiledModels');

    this.generateBoundingBox();
  }

  /**
   *  Loads the root model and extracts the geometry center point.
   */
  async activate() {
    const tileNode = this.cesiumTileSetHierarchy.rootTileSet?.root;
    if (!isDefined(tileNode)) {
      return;
    }

    if (tileNode.content?.uri) {
      const rootNodeUrl = createTileNodeDataUri(tileNode, tileNode.content.uri);
      this.cache.queue(rootNodeUrl);

      await this.loadTileNodes([tileNode]);

      const rootModel = this.cache.get(rootNodeUrl) as Object3D;
      this.geometryBoundingBox = new Box3().setFromObject(rootModel);
    } else if (tileNode.boundingVolume?.box) {
      // If the root node is missing data we grab the bounding box from the bounding volume instead.
      this.geometryBoundingBox = new Box3().setFromArray(tileNode.boundingVolume?.box);
    }
  }

  /**
   *  Filters the chunks based on the camera frustum.
   *  Currently only return the root chunk. Will be implemented later on.
   */
  async loadLodChunks(camera: SkyviewCamera) {
    if (!this.resolveLoadChunks()) {
      return;
    }
    this.lastTick = Date.now();

    // Sorts the nodes to ensure we load the highest node level closest to the camera ray first
    // The tiles are around the model's origo, which is placed at its position in the scene
    const nodesToLoad = await this.cesiumTileSetHierarchy.filterTileNodesToBeLoaded(
      camera,
      this.cache,
      this.position,
    );

    nodesToLoad.sort((a: TileNodeLoadResponse, b: TileNodeLoadResponse) => {
      if (a.index === b.index) {
        return a.distanceToRay - b.distanceToRay;
      }
      return b.index - a.index;
    });

    // Do not queue any tile nodes if the queue is full
    if (this.tileNodeLoadQueue.size >= this.maxLoadQueueSize) {
      return;
    }

    // Do only add up to the queue limit of chunks to be loaded simultaneously
    const nodesInQueue = this.maxLoadQueueSize - this.tileNodeLoadQueue.size;
    const tileNodesToLoad = nodesToLoad.slice(0, Math.min(nodesToLoad.length, nodesInQueue));

    await this.loadTileNodes(
      tileNodesToLoad.map((node: TileNodeLoadResponse) => {
        this.cache.queue(createTileNodeDataUri(node.node, node.node.content?.uri!));
        return node.node;
      }),
    );
  }

  /**
   *  Attaches the point clouds chunks that are loaded and within the frustum
   */
  cullLodChunks(camera: SkyviewCamera) {
    const newChildren = this.cesiumTileSetHierarchy.resolveTileNodesToBeRendered(
      camera,
      this.cache,
      this.position,
    );

    this.remove(...this.children);
    if (newChildren && newChildren.length > 0) {
      this.add(...newChildren);
    } else {
      const nodeWithContent = this.cesiumTileSetHierarchy.rootTileSet?.root
        ? getNodeWithContent(this.cesiumTileSetHierarchy.rootTileSet?.root)
        : undefined;
      if (isDefined(nodeWithContent)) {
        const modelData = this.cache.get(nodeWithContent.content!.uri!);
        if (isDefined(modelData)) {
          this.add(modelData as Object3D);
        }
      }
    }
  }

  isLoading(): boolean {
    return getWebWorkerManager().isLoading();
  }

  /** Every hit of the ray, front-most first. */
  pick(ray: Raycaster): Intersection[] {
    const intersections = ray.intersectObject(this, true);
    return intersections.filter((intersection) => intersection.object.name !== 'temp');
  }

  intersect(ray: Raycaster): Intersection[] {
    return toPointPositions(ray, this.pick(ray));
  }

  available() {
    return true;
  }

  /** The bounds in the scene, at the model's position. */
  getBoundingBox() {
    return this.boundingBox.clone().translate(this.position);
  }

  getGeometryBoundingBox() {
    return this.geometryBoundingBox;
  }

  getOrigo() {
    return this.origo;
  }

  clearCustom() {
    this.cache.clearCache();

    getWebWorkerManager().stopChunks('tiledModels');
    getWebWorkerManager().init('tiledModels');
  }

  private async loadTileNodes(tileNodes: TileNode[]) {
    // Declare the nodes to be loaded as queued
    for (const tileNode of tileNodes) {
      const tileNodeUri = createTileNodeDataUri(tileNode, tileNode.content?.uri!);
      this.tileNodeLoadQueue.set(tileNodeUri, tileNode);
    }

    const b3dmTiledModes = await getWebWorkerManager().spawnTiledModelLoader(tileNodes);

    for (const b3dmTileNode of b3dmTiledModes) {
      const tileNodeUri = createTileNodeDataUri(b3dmTileNode.node, b3dmTileNode.node.content?.uri!);

      if (b3dmTileNode.buffer) {
        const glbObject = await CesiumLoader.instance.loadB3DM(Buffer.from(b3dmTileNode.buffer));
        this.cache.addScanChunk(tileNodeUri, glbObject.glb.scene);
      }

      // Clear the queue when a tile node is finished loading
      this.tileNodeLoadQueue.delete(tileNodeUri);
    }
  }

  private generateBoundingBox() {
    const node = getNodeWithContent(this.cesiumTileSetHierarchy.rootTileSet?.root!);
    const sphere = extractTileNodeBoundingSphere(node!);
    this.boundingBox = sphere.getBoundingBox(new Box3());
  }

  private resolveLoadChunks() {
    // Limits traversing the hierarchy a set number of times per second
    return Date.now() - this.lastTick > 200;
  }
}
